import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {
  ErrorIconComponent,
  ModalComponent,
  SpinnerComponent
} from 'remarketing-ui-components-lib';
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import {
  AuctionItemSell,
  ColorOption,
  EditPostingService,
  EditPostingState,
  LaunchDarklyService,
  PostingDataResponseModel,
  StorageLocation,
  StorageLocationService
} from 'remarketing-angular-library';
import { map, startWith, take, takeUntil } from 'rxjs/operators';
import { forkJoin, Observable, Subject } from 'rxjs';
import { EditPostingUtilsService } from 'src/sell/lib/posting/services/edit-posting-utils.service';
import { Store } from '@ngxs/store';
import { AnalyticsService } from 'smartauction-frontend-micro-library';
import {
  MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
  MatAutocomplete, MatAutocompleteModule,
  MatAutocompleteSelectedEvent
} from '@angular/material/autocomplete';
import {
  VehicleStatusBadgeComponent
} from '../../../../../shared/components/vehicle-status-badge/vehicle-status-badge.component';
import { NgxMaskModule } from 'ngx-mask';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';

@Component({
    selector: 'app-edit-listing-ground-modal',
    templateUrl: './edit-listing-ground-modal.component.html',
    styleUrls: ['./edit-listing-ground-modal.component.scss'],
    providers: [
        {
            provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
            useValue: { overlayPanelClass: 'location-autocomplete-panel' }
        }
    ],
    imports: [
        ModalComponent,
        SpinnerComponent,
        ErrorIconComponent,
        VehicleStatusBadgeComponent,
        ReactiveFormsModule,
        NgxMaskModule,
        CommonModule,
        MatAutocompleteModule,
        MatInputModule
    ]
})
export class EditListingGroundModalComponent implements OnInit, OnDestroy {
  @ViewChild(ModalComponent) modal: ModalComponent;
  @ViewChild('autocomplete') autocomplete: MatAutocomplete;
  @ViewChild('searchableLocationInput') searchableLocationInput: ElementRef;
  @Input() groundedValue: boolean;
  @Output() groundedValueChanged = new EventEmitter<any>();

  private readonly destroyed$ = new Subject();
  private readonly DEFAULT = 'default';

  locationOptions: StorageLocation[];
  filteredOptions: Observable<StorageLocation[]>;
  vehicleLocationSearchControl = new FormControl<string | StorageLocation>('');

  modalType: string;
  auctionItem: AuctionItemSell;
  editListingForm: UntypedFormGroup;
  regPatternForMultiples = '^[1-9]+[0-9]*00$|^0$';
  defaultOdometer = 0;
  defaultOpeningPrice = 0;
  isLoading = true;
  exteriorColorOptions: ColorOption[];
  postingData: PostingDataResponseModel;
  isSuccessResponse = false;
  isError = false;
  errorMessage: string;
  isSearchableLocationInputFocused = false;

  editListingGroundModalTranslations = {
    textEditListingHeader: '',
    textPrice: '',
    textOdometerMiles: '',
    textVehicleLocation: '',
    textExteriorColor: '',
    textVehicleGrounded: '',
    textUpdateListing: '',
    textCancel: '',
    textVehicleDescription: '',
    textVin: '',
    textReservedFor: '',
    textReservedLabel: '',
    textSelect: '',
    ariaLabelUpdateSelectedModal: '',
    labelOdometerMiles: '',
    textClose: '',
    textSuccessHeader: '',
    textSuccessMessage: '',
    cantCompleteRequestError: '',
    locationInputPlaceholderText: '',
    noResultsMatchingSearchText: ''
  };

  get isOdometerInvalid(): boolean {
    return (
      this.editListingForm?.get('odometer').touched &&
      (this.odometerMiles === null || this.odometerMiles === undefined || this.odometerMiles <= this.defaultOdometer)
    );
  }

  get odometerMiles() {
    return this.editListingForm.get('odometer').value;
  }

  get isInvalidInputOpeningPrice(): boolean {
    return (
      this.editListingForm?.get('price').touched &&
      (!this.editListingForm?.get('price').value || this.isInValidDefaultOpeningPrice)
    );
  }

  get isInValidDefaultOpeningPrice(): boolean {
    const priceValue = this.editListingForm?.get('price').value;
    return !!priceValue && priceValue < this.defaultOpeningPrice;
  }

  get isFrenchCanadian(): boolean {
    const isFrenchLanguage = this.translocoService.getActiveLang().includes('fr');
    return isFrenchLanguage && this.isCanadianTenantEnabled;
  }

  get isCanadianTenantEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get isSearchableGroundingLocationEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-searchableGroundingLocationEnabled', false);
  }

  get isReservationTextBuilt(): boolean {
    return (
      this.editListingGroundModalTranslations.textReservedFor !== null &&
      this.editListingGroundModalTranslations.textReservedFor !== undefined &&
      this.editListingGroundModalTranslations.textReservedFor.length > 0
    );
  }

  get isGrounded(): boolean {
    return this.editListingForm.get('vehicleGrounded').value;
  }

  get badgeStatus(): string {
    return this.auctionItem?.auctionItemStatus;
  }

  get dealershipName(): string {
    return this.auctionItem?.reservation?.reservedForDealerName;
  }

  constructor(
    private readonly translocoService: TranslocoService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly storageLocationService: StorageLocationService,
    private readonly store: Store,
    private readonly editPostingService: EditPostingService,
    private readonly editPostingUtilsService: EditPostingUtilsService,
    private readonly analyticsService: AnalyticsService
  ) {}

  ngOnInit(): void {
    this.getTranslations();
    this.editListingForm = new UntypedFormGroup({
      price: new UntypedFormControl(),
      odometer: new UntypedFormControl(),
      vehicleLocation: new UntypedFormControl(undefined),
      exteriorColor: new UntypedFormControl(this.DEFAULT),
      vehicleGrounded: new UntypedFormControl(false)
    });
  }

  displayLocationNameInAutocomplete(location: StorageLocation): string {
    return location && location?.name ? location.name : '';
  }

  onOptionSelected(event: MatAutocompleteSelectedEvent) {
    const selectedLocation = event.option.value;
    this.editListingForm.get('vehicleLocation').setValue(selectedLocation);
  }

  handleInputValueOnBlur() {
    // We need this timeout due to a common race condition between autocomplete optionSelected event and the search inputs blur event in angular material v14
    // When an option is selected from the dropdown and optionSelected event triggers, we need its values set before the blur of the search input
    setTimeout(() => {
      // If a user only searches locations and does not select a dropdown option, the current input value would be a string
      // in the string case we need to reset the value of the input for display purposes to the last selected location name
      // If the user actually selects a location from the dropdown options, the current search input value is an object
      const currentLocationSetOnForm = this.editListingForm.get('vehicleLocation').value;
      const currentLocationSearchInputValue = this.vehicleLocationSearchControl.value;
      if (typeof currentLocationSearchInputValue !== 'object') {
        this.vehicleLocationSearchControl.setValue(currentLocationSetOnForm);
      }
    }, 150);
  }

  focusLocationInput(): void {
    this.searchableLocationInput.nativeElement.focus();
  }

  show(modalType: string, auctionItem: AuctionItemSell) {
    this.isError = false;
    this.isLoading = true;
    this.isSuccessResponse = false;
    this.modalType = modalType;
    this.auctionItem = auctionItem;
    this.setModalHeaderVehicleDetails(auctionItem);
    this.getFormData();
    this.modal.show();
  }

  getFormData() {
    const sellerOeUuid = this.store.selectSnapshot(EditPostingState.selectedSellerOeUuid);
    const dealershipUuid = this.editPostingUtilsService.getSelectedDealershipUuid(sellerOeUuid);

    forkJoin([
      this.storageLocationService.getStorageLocationData(dealershipUuid),
      this.editPostingService.getPostingData(this.auctionItem.inventoryItemUuid)
    ])
      .pipe(take(1))
      .subscribe(
        ([storageLocationResponse, editPostingResponse]) => {
          this.locationOptions = this.alphabetizeLocationResponseByLocationName(storageLocationResponse);
          this.postingData = editPostingResponse;
          this.exteriorColorOptions = editPostingResponse.inventoryItem?.vehicleOptions.exteriorColors;
          this.buildForm();
        },
        error => {
          console.error(error);
          this.isError = true;
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        }
      );
  }

  alphabetizeLocationResponseByLocationName(storageLocationResponse) {
    return storageLocationResponse.sort((firstLocation, secondLocation) => {
      const firstLocationName = firstLocation.name.toLowerCase();
      const secondLocationName = secondLocation.name.toLowerCase();
      if (firstLocationName < secondLocationName) {
        return -1;
      }
      if (firstLocationName > secondLocationName) {
        return 1;
      }
      return 0;
    });
  }

  buildForm() {
    const groundedDate = this.auctionItem?.groundedDate;
    const installedExteriorColor = this.postingData.inventoryItem.vehicleOptions.exteriorColors?.find(
      color => color.installed === true
    );
    const existingLocationFromPostingData = this.postingData?.inventoryItem?.storageLocation;
    this.editListingForm.patchValue({
      price: this.postingData?.inventoryItem?.valuation?.openingPrice,
      odometer: this.postingData?.inventoryItem?.vehicleInformation?.odometer,
      vehicleLocation: this.locationOptions.find(
        option => option.locationUuid === existingLocationFromPostingData?.locationUuid
      ),
      exteriorColor: installedExteriorColor ? installedExteriorColor?.colorCode : this.DEFAULT,
      vehicleGrounded: groundedDate !== null && groundedDate !== undefined && groundedDate.length > 0
    });

    if (this.isSearchableGroundingLocationEnabled) {
      this.initializeVehicleLocationSearchableInputData(existingLocationFromPostingData);
    }
  }

  initializeVehicleLocationSearchableInputData(storageLocation: StorageLocation) {
    this.vehicleLocationSearchControl.setValue(storageLocation);
    this.initializeFilteredLocationOptions(storageLocation.name);
  }

  initializeFilteredLocationOptions(initialLocationName: string): void {
    this.filteredOptions = this.vehicleLocationSearchControl.valueChanges.pipe(
      startWith(initialLocationName),
      map(locationSearchValue => {
        const locationName = typeof locationSearchValue === 'string' ? locationSearchValue : locationSearchValue?.name;
        return this.isLocationNameValid(locationName)
          ? this.filterLocations(locationName as string)
          : this.locationOptions.slice();
      })
    );
  }

  isLocationNameValid(name: string): boolean {
    return name !== null && name !== undefined && name.length > 0;
  }

  filterLocations(locationName: string): StorageLocation[] {
    const filterValue = locationName.toLowerCase();
    return this.locationOptions.filter(option => option.name.toLowerCase().includes(filterValue));
  }

  setSearchableLocationFocused(value: boolean) {
    this.isSearchableLocationInputFocused = value;
  }

  setModalHeaderVehicleDetails(auctionItem: AuctionItemSell) {
    this.editListingGroundModalTranslations.textVin =
      auctionItem?.itemDetail?.vin && auctionItem?.itemDetail?.vin.length > 0 ? auctionItem?.itemDetail.vin : undefined;
    this.editListingGroundModalTranslations.textVehicleDescription = this.buildVehicleDescription(auctionItem);
    this.editListingGroundModalTranslations.textReservedFor = this.buildReservedForText(auctionItem);
  }

  buildVehicleDescription(auctionItem: AuctionItemSell): string {
    const modelYear = auctionItem?.itemDetail?.modelYear ? auctionItem.itemDetail.modelYear : null;
    const make = auctionItem?.itemDetail?.make ? auctionItem.itemDetail.make : null;
    const model = auctionItem?.itemDetail?.model ? auctionItem.itemDetail.model : null;
    const stringValues = [modelYear, make, model];
    const emptySpace = ' ';
    return stringValues.filter(value => value !== null).join(emptySpace);
  }

  buildReservedForText(auctionItem: AuctionItemSell): string {
    const reservation = auctionItem?.reservation;
    if (reservation && reservation?.reservedForUuid && reservation?.reservedForUuid.length > 0) {
      return `${this.editListingGroundModalTranslations.textReservedLabel} ${reservation.reservedForUuid}`;
    } else {
      return undefined;
    }
  }

  close() {
    this.editListingForm.reset();
    this.modal.close();
  }

  updateListing() {
    this.isLoading = true;
    const priceValue = this.editListingForm.get('price').value;
    const odometerValue = this.editListingForm.get('odometer').value;
    const vehicleLocationValue = this.editListingForm.get('vehicleLocation').value;
    const exteriorColorValue = this.editListingForm.get('exteriorColor').value;
    const vehicleGroundedValue = this.editListingForm.get('vehicleGrounded').value;
    const payload = {
      data: {
        grounded: vehicleGroundedValue,
        inventoryItemUuid: this.auctionItem.inventoryItemUuid,
        auctionItemUuid: this.auctionItem.auctionItemUuid,
        price: priceValue || null,
        odometer: odometerValue || null,
        exteriorColor: exteriorColorValue || null,
        storageLocation: vehicleLocationValue || null
      },
      metadata: {
        // If no trim was selected at posting, we don't need to validate trim on updates.
        ignoreWarnings: ['TRIM_NOT_SELECTED'],
        inspectionOption: null,
        lastModifiedTimeStampInMillis: this.postingData.metadata?.lastModifiedTimeStampInMillis
      }
    };

    this.editPostingService
      .editGroundedListing(payload)
      .pipe(take(1))
      .subscribe({
        next: response => {
          // If successful close modal.
          if (response) {
            this.groundedValueChanged.emit(response);
            this.analyticsEditListingSuccess();
            this.isSuccessResponse = true;
          }
        },
        error: error => {
          if (error) {
            if (error.error.errors[0].code === 'F-REMPLT-IIS-OPDET') {
              this.mapErrorTranslations(error.error.errors[0].code);
            } else {
              this.errorMessage = this.editListingGroundModalTranslations.cantCompleteRequestError;
            }
            this.isError = true;
            this.analyticsService.track('customError', error.error.errors[0].title, null, {
              name: 'edit-listing-ground-modal-error',
              attr: 'edit-listing-ground-modal-error'
            });
          }
          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
        }
      });
  }

  analyticsEditListingSuccess() {
    const openingPrice = this.postingData
      ? this.postingData.inventoryItem.valuation.openingPrice
      : this.defaultOpeningPrice;
    const formPrice = this.editListingForm?.get('price').value;
    const priceChange = openingPrice === formPrice ? 'no' : 'yes';
    const isGrounded = this.editListingForm?.get('vehicleGrounded').value ? 'yes' : 'no';

    this.analyticsService.track('customEvent', {
      name: 'editListingGround',
      type: 'Custom Event',
      category: 'UserInteraction',
      attr: {
        priceChange: priceChange,
        vehicleGroundedCheckboxSelected: isGrounded
      }
    });
  }

  analyticsHidePiiLocationDropDown(): void {
    this.analyticsService.trackEvent('dropdown', 'Vehicle Location');
  }

  analyticsHidePiiColorDropDown(): void {
    this.analyticsService.trackEvent('dropdown', 'Exterior Color');
  }

  toggleGroundedValue(): void {
    const currentGroundedValue = this.editListingForm.get('vehicleGrounded').value;
    this.analyticsService.trackEvent('checkbox', 'Selected Vehicle Grounded - ' + !currentGroundedValue);
    this.editListingForm.patchValue({ vehicleGrounded: !currentGroundedValue });
  }

  mapErrorTranslations(errorCode) {
    const keyParams = {
      error: {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(take(1))
      .subscribe(translate => {
        this.errorMessage = translate[0][errorCode];
      });
  }

  getTranslations() {
    const keyParams = {
      'sellModule.manageInventory.editListingGroundModal': {},
      'sellModule.manageInventory.actionListModal.updateSelectedModal': {},
      'sellModule.posting.descriptionForm': {},
      'app.layout': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.editListingGroundModalTranslations.textEditListingHeader = translate[0].textEditListingHeader;
        this.editListingGroundModalTranslations.textPrice = translate[0].textPrice;
        this.editListingGroundModalTranslations.textOdometerMiles = translate[0].textOdometerMiles;
        this.editListingGroundModalTranslations.textVehicleLocation = translate[0].textVehicleLocation;
        this.editListingGroundModalTranslations.textExteriorColor = translate[0].textExteriorColor;
        this.editListingGroundModalTranslations.textVehicleGrounded = translate[0].textVehicleGrounded;
        this.editListingGroundModalTranslations.textUpdateListing = translate[0].textUpdateListing;
        this.editListingGroundModalTranslations.textCancel = translate[0].textCancel;
        this.editListingGroundModalTranslations.textReservedLabel = translate[0].textReservedLabel;
        this.editListingGroundModalTranslations.textSelect = translate[0].textSelect;
        this.editListingGroundModalTranslations.ariaLabelUpdateSelectedModal =
          translate[1].ariaLabelUpdateSelectedModal;
        this.editListingGroundModalTranslations.labelOdometerMiles = translate[2].labelOdometerMiles;
        this.editListingGroundModalTranslations.textClose = translate[0].textClose;
        this.editListingGroundModalTranslations.textSuccessHeader = translate[0].textSuccessHeader;
        this.editListingGroundModalTranslations.textSuccessMessage = translate[0].textSuccessMessage;
        this.editListingGroundModalTranslations.cantCompleteRequestError = translate[3].cantCompleteRequestError;
        this.editListingGroundModalTranslations.locationInputPlaceholderText =
          translate[0].locationInputPlaceholderText;
        this.editListingGroundModalTranslations.noResultsMatchingSearchText = translate[0].noResultsMatchingSearchText;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
